import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Form, Select, message } from 'antd';
import axios from 'axios';
import AuthContext from '../../../services/Auth';
import {
  ALLOCATE_RESOURCE_URL,
  GET_ASSIGNED_USERS_URL,
  GET_USER_PROJECTS_URL,
} from '../../../constant';
import { buildResourceAllocationPayload } from '../../../utils/resourceAllocationPayload';
import { uniqueBy } from '../../../utils/uniqueOptions';
import '../../../assets/css/addAllocation.scss';

const { RangePicker } = DatePicker;

const defaultPercentageOptions = Array.from({ length: 21 }, (_, index) => {
  const value = `${index * 5}%`;
  return {
    value,
    label: value,
  };
});

const normalizeUserProjects = (result) => {
  if (!result) {
    return [];
  }

  if (Array.isArray(result)) {
    return result;
  }

  if (Array.isArray(result.data)) {
    return result.data;
  }

  if (Array.isArray(result.projects)) {
    return result.projects;
  }

  if (result.projectId || result.id) {
    return [result];
  }

  return [];
};

const parsePercentage = (value) => Number(String(value || '').replace('%', '').trim());

const toProjectOption = (project) => {
  const projectId = project.projectId || project.id;
  const projectName =
    project.projectName || project.name || project.title || `Project ${projectId}`;
  const projectCode = project.projectCode || project.code;

  return {
    value: projectId,
    label: projectCode ? `${projectName} (${projectCode})` : projectName,
  };
};

const AddAllocation = () => {
  const [form] = Form.useForm();
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allocatedByUserId, setAllocatedByUserId] = useState(null);

  const loggedInUser = AuthContext.getCachedUser();
  const loggedInUserEmail = (loggedInUser?.userName || loggedInUser?.profile?.unique_name || '')
    .toLowerCase();
  const assignedByName =
    loggedInUser?.profile?.name ||
    loggedInUser?.profile?.unique_name ||
    loggedInUser?.userName ||
    '';

  const assignedByOptions = useMemo(
    () =>
      assignedByName
        ? [
            {
              value: assignedByName,
              label: assignedByName,
            },
          ]
        : [],
    [assignedByName]
  );

  const selectedPerson = Form.useWatch('personName', form);

  useEffect(() => {
    const fetchAssignedUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await axios.get(
          `${GET_ASSIGNED_USERS_URL}&allocatorEmail=${encodeURIComponent(loggedInUserEmail)}`
        );
        const payload = response.data;
        const data = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.users)
            ? payload.users
            : Array.isArray(payload?.data)
              ? payload.data
              : [];

        const allocatorId =
          payload?.allocatedBy ??
          payload?.AllocatedBy ??
          payload?.allocatorUserId ??
          payload?.currentUserId ??
          null;

        const uniqueData = uniqueBy(data, (user) => user.userId ?? user.UserId);

        const options = uniqueData.map((user) => ({
          value: user.userId ?? user.UserId,
          internalUserId: user.id ?? user.Id,
          label: user.userName ?? user.UserName,
          userEmail: user.userEmail ?? user.UserEmail,
          userName: user.userName ?? user.UserName,
        }));

        const resolvedAllocatorId =
          allocatorId ??
          options.find((user) => user.userEmail?.toLowerCase() === loggedInUserEmail)
            ?.internalUserId ??
          null;

        setAllocatedByUserId(resolvedAllocatorId);
        setPeopleOptions(options);
      } catch (error) {
        // console.error('Unable to load assigned users', error);
        message.error('Unable to load assigned users. Please refresh the page.');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchAssignedUsers();
  }, [loggedInUserEmail]);

  useEffect(() => {
    const fetchUserProjects = async () => {
      if (!selectedPerson) {
        setProjectOptions([]);
        return;
      }

      setLoadingProjects(true);
      form.setFieldsValue({ project: undefined });

      try {
        const response = await axios.get(
          `${GET_USER_PROJECTS_URL}${encodeURIComponent(selectedPerson)}`
        );
        const projects = uniqueBy(
          normalizeUserProjects(response.data),
          (project) => project.projectId ?? project.id
        );

        setProjectOptions(projects.map(toProjectOption));
      } catch (error) {
        // console.error('Unable to load projects for selected user', error);
        message.error('Unable to load projects for selected user.');
        setProjectOptions([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchUserProjects();
  }, [selectedPerson]);

  const onFinish = async (values) => {
    const selectedUser = peopleOptions.find((user) => user.value === values.personName);
    const selectedProject = projectOptions.find((project) => project.value === values.project);

    if (!selectedUser) {
      message.error('Selected user is not valid.');
      return;
    }

    if (!selectedProject) {
      message.error('Selected project is not valid.');
      return;
    }

    if (!selectedUser.internalUserId) {
      message.error('Selected user is missing an internal user id.');
      return;
    }

    if (!allocatedByUserId) {
      message.error('Unable to determine your user id for allocation. Please refresh the page.');
      return;
    }

    const [startDate, endDate] = values.allocationDateRange || [];

    if (!startDate || !endDate) {
      message.error('Please select the allocation date range.');
      return;
    }

    const userId = Number(selectedUser.internalUserId);
    const projectId = Number(selectedProject.value);
    const allocatedBy = Number(allocatedByUserId);
    const billablePercentage = parsePercentage(values.billablePercentage);
    const nonBillablePercentage = parsePercentage(values.nonBillablePercentage);

    if (
      [userId, projectId, allocatedBy, billablePercentage, nonBillablePercentage].some(
        (value) => Number.isNaN(value)
      )
    ) {
      message.error('Allocation values are invalid. Please reselect the form fields.');
      return;
    }

    const allocationBody = buildResourceAllocationPayload({
      userId,
      projectId,
      startDate,
      endDate,
      billablePercentage,
      nonBillablePercentage,
      allocatedBy,
    });

    setSubmitting(true);
    try {
      await axios.post(ALLOCATE_RESOURCE_URL, allocationBody);
      message.success('Allocation saved successfully.');
      form.resetFields();
      form.setFieldsValue({
        assignedBy: assignedByName || undefined,
        allocationDateRange: [],
      });
      setProjectOptions([]);
    } catch (error) {
      // console.error('Unable to save allocation', error?.response?.data || error);
      const responseData = error?.response?.data;
      const apiMessage =
        responseData?.Message ||
        responseData?.message ||
        responseData?.error ||
        (typeof responseData === 'string' ? responseData : null);
      message.error(apiMessage || 'Unable to save allocation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="table-container add-allocation-page">
      <div className="add-allocation-shell">
        <div className="add-allocation-header">
          <h2>Add Allocation</h2>
          <p>
            Assign a person to a project with a date range and billable split.
          </p>
        </div>

        <Form
          form={form}
          layout="vertical"
          className="add-allocation-form"
          initialValues={{
            assignedBy: assignedByName || undefined,
          }}
          onFinish={onFinish}
        >
          <div className="add-allocation-grid">
            <Form.Item
              label="Person Name"
              name="personName"
              rules={[{ required: true, message: 'Please select a person name.' }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Select or type a person name"
                options={peopleOptions.map(({ value, label }) => ({ value, label }))}
                optionFilterProp="label"
                className="add-allocation-select"
                onChange={() => {
                  form.setFieldsValue({ project: undefined });
                }}
                loading={loadingUsers}
              />
            </Form.Item>

            <Form.Item
              label="Project"
              name="project"
              dependencies={['personName']}
              rules={[
                { required: true, message: 'Please select a project.' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.resolve();
                    }

                    if (!getFieldValue('personName')) {
                      return Promise.reject(
                        new Error('Please select a person before choosing a project.')
                      );
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select
                showSearch
                allowClear
                placeholder={
                  selectedPerson
                    ? 'Select or type a project'
                    : 'Choose a person first to view projects'
                }
                options={projectOptions}
                optionFilterProp="label"
                className="add-allocation-select"
                disabled={!selectedPerson || loadingProjects}
                loading={loadingProjects}
              />
            </Form.Item>

            <Form.Item
              label="Allocation Date Range"
              required
              className="add-allocation-span-2"
            >
              <div className="add-allocation-range-shell">
                <div className="add-allocation-range-labels">
                  <span>From</span>
                  <span>To</span>
                </div>
                <Form.Item
                  name="allocationDateRange"
                  noStyle
                  rules={[
                    { required: true, message: 'Please select the allocation date range.' },
                  ]}
                >
                  <RangePicker
                    format="DD/MM/YYYY"
                    className="add-allocation-range"
                    placeholder={['DD/MM/YYYY', 'DD/MM/YYYY']}
                    inputReadOnly={false}
                    allowClear
                    onChange={(dates) => {
                      form.setFieldsValue({ allocationDateRange: dates || [] });
                    }}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              label="Billable Percentage"
              name="billablePercentage"
              dependencies={['nonBillablePercentage']}
              rules={[
                { required: true, message: 'Please select the billable percentage.' },
                {
                  validator(_, value) {
                    const numericValue = parsePercentage(value);

                    if (!value) {
                      return Promise.resolve();
                    }

                    if (numericValue < 0 || numericValue > 100) {
                      return Promise.reject(
                        new Error('Billable percentage must be between 0% and 100%.')
                      );
                    }

                    const otherPercent = parsePercentage(form.getFieldValue('nonBillablePercentage'));
                    if (!Number.isNaN(otherPercent) && numericValue + otherPercent > 100) {
                      return Promise.reject(
                        new Error('Billable and non-billable together must not exceed 100%.')
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Select or type billable percentage"
                options={defaultPercentageOptions}
                optionFilterProp="label"
                className="add-allocation-select"
              />
            </Form.Item>

            <Form.Item
              label="Non Billable Percentage"
              name="nonBillablePercentage"
              dependencies={['billablePercentage']}
              rules={[
                { required: true, message: 'Please select the non billable percentage.' },
                {
                  validator(_, value) {
                    const numericValue = parsePercentage(value);

                    if (!value) {
                      return Promise.resolve();
                    }

                    if (numericValue < 0 || numericValue > 100) {
                      return Promise.reject(
                        new Error('Non billable percentage must be between 0% and 100%.')
                      );
                    }

                    const otherPercent = parsePercentage(form.getFieldValue('billablePercentage'));
                    if (!Number.isNaN(otherPercent) && numericValue + otherPercent > 100) {
                      return Promise.reject(
                        new Error('Billable and non-billable together must not exceed 100%.')
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Select or type non billable percentage"
                options={defaultPercentageOptions}
                optionFilterProp="label"
                className="add-allocation-select"
              />
            </Form.Item>

            <Form.Item
              label="Assigned By"
              name="assignedBy"
              required
              className="add-allocation-span-2"
              rules={[{ required: true, message: 'Assigned by is required.' }]}
            >
              <Select
                showSearch
                options={assignedByOptions}
                optionFilterProp="label"
                disabled
                className="add-allocation-select"
              />
            </Form.Item>
          </div>

          <div className="add-allocation-actions">
            <Button
              className="add-allocation-clear-button"
              onClick={() => {
                form.resetFields();
                form.setFieldsValue({
                  assignedBy: assignedByName || undefined,
                  allocationDateRange: [],
                });
              }}
            >
              Clear
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="add-allocation-submit-button"
              loading={submitting}
              disabled={submitting}
            >
              Save Allocation
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddAllocation;
